import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import Dashboard from "@/pages/Dashboard";
import TranslationEditor from "@/pages/TranslationEditor";
import TermBaseManager from "@/pages/TermBaseManager";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export { API };

function AppContent() {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      console.error("Failed to fetch projects:", e);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleProjectSelect = useCallback((project) => {
    setCurrentProject(project);
    navigate(`/editor/${project.id}`);
  }, [navigate]);

  const handleProjectCreated = useCallback((project) => {
    setProjects(prev => [project, ...prev]);
    setCurrentProject(project);
    toast.success("Project imported successfully");
    navigate(`/editor/${project.id}`);
  }, [navigate]);

  const handleDeleteProject = useCallback(async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        navigate('/');
      }
      toast.success("Project deleted");
    } catch (e) {
      toast.error("Failed to delete project");
    }
  }, [currentProject, navigate]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar
        currentProject={currentProject}
        onNavigateHome={() => { setCurrentProject(null); navigate('/'); }}
      />
      <main className="pt-14">
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                projects={projects}
                onProjectSelect={handleProjectSelect}
                onProjectCreated={handleProjectCreated}
                onDeleteProject={handleDeleteProject}
                onRefresh={fetchProjects}
              />
            }
          />
          <Route
            path="/editor/:projectId"
            element={
              <TranslationEditor
                project={currentProject}
                setProject={setCurrentProject}
              />
            }
          />
          <Route
            path="/termbase/:projectId"
            element={
              <TermBaseManager
                project={currentProject}
              />
            }
          />
        </Routes>
      </main>
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: '#12141A',
            border: '1px solid #2A2F3A',
            color: '#E0E0E0',
            fontFamily: 'Exo 2, sans-serif',
          },
        }}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
